import {Description, DialogTitle} from "@headlessui/react";
import {CheckCircleIcon} from "@heroicons/react/20/solid";
import {Dialog} from "catalyst";
import type {ReactNode} from "react";
import {useAppDensity} from "../app/AppContext";
import {OtsBrand, OtsColorSchemePreference, OtsThemeRoot} from "../theme/OtsTheme";
import {OtsActionButton} from "./OtsButton";
import type {OtsDialogSize} from "./OtsDialog";
import {OtsForm, OtsFormActions} from "./OtsForm";
import {OtsInlineAlert} from "./OtsInlineAlert";

export type OtsFormDialogTone = "primary" | "danger" | "success";

/** What replaces the form once it has gone through, e.g. "Password updated". */
export type OtsFormDialogCompletion = {
  title: ReactNode;
  description?: ReactNode;
  /** Defaults to a check mark. */
  icon?: ReactNode;
  /** The one button left; it closes the dialog. Defaults to "Done". */
  doneLabel?: ReactNode;
};

export type OtsFormDialogProps = {
  open: boolean;
  /** Cancel, Escape, a click outside, or Done after {@link completion}. Never fires while submitting. */
  onClose: () => void;
  /** Submit button or Enter in a field. Validation is the caller's; return early to stay open. */
  onSubmit: () => void;
  title: ReactNode;
  description?: ReactNode;
  /** Shown in a tinted circle beside the title; omit for a title alone. */
  icon?: ReactNode;
  /** Tints the icon; "danger" also makes the submit button destructive. */
  tone?: OtsFormDialogTone;
  /** Names the form; defaults to the title when that's a string. */
  formLabel?: string;
  /** The fields. A confirmation may have none. */
  children?: ReactNode;
  /** A failed submit, shown above the actions. The dialog stays open so it can be retried. */
  error?: ReactNode;
  submitLabel?: ReactNode;
  /** Button text while submitting; defaults to {@link submitLabel}. */
  submittingLabel?: ReactNode;
  /** Accessible name of the spinner while submitting. */
  progressLabel?: string;
  cancelLabel?: ReactNode;
  /** Holds the submit button, e.g. until the caller's input is complete. */
  submitDisabled?: boolean;
  /** Locks the dialog open, disables Cancel, and shows progress on the submit button. */
  submitting?: boolean;
  /** Starts focus on Cancel rather than the first field; the safe default for confirmations. */
  autoFocusCancel?: boolean;
  /** Replaces the header, fields and actions once the work is done. */
  completion?: OtsFormDialogCompletion;
  size?: OtsDialogSize;
  /** Tighter spacing and smaller type for dense hosts such as WaterGo. Defaults to the app density. */
  compact?: boolean;
  brand?: OtsBrand;
  /**
   * Light or dark. Defaults to "system" -- the host page's theme, else the OS -- for a dialog opened
   * on its own. A page opening it from inside its own theme root passes "inherit" to match itself.
   */
  colorScheme?: OtsColorSchemePreference;
};

const toneClasses: Record<OtsFormDialogTone, string> = {
  primary: "bg-[var(--ots-primary-container)] text-[var(--ots-primary)]",
  danger: "bg-[var(--ots-danger-container)] text-[var(--ots-on-danger-container)]",
  success: "bg-[var(--ots-success-container)] text-[var(--ots-success)]",
};

const titleFontClassName = "font-[family-name:var(--ots-font-display)] font-semibold text-[var(--ots-secondary)]";

/**
 * Themed shell for dialogs that ask for something and submit it: an optional icon beside the title,
 * the fields, and Cancel/Submit at the bottom in place of a Close button. Dismissal is blocked while
 * submitting so a save on its way to the server isn't dropped. Use OtsDialog for inspectors instead.
 */
export const OtsFormDialog = ({
  open,
  onClose,
  onSubmit,
  title,
  description,
  icon,
  tone = "primary",
  formLabel,
  children,
  error,
  submitLabel = "Submit",
  submittingLabel,
  progressLabel,
  cancelLabel = "Cancel",
  submitDisabled,
  submitting = false,
  autoFocusCancel = false,
  completion,
  size = "md",
  compact: compactProp,
  brand,
  colorScheme,
}: OtsFormDialogProps) => {
  const density = useAppDensity();
  const compact = compactProp ?? density === "compact";

  const close = () => {
    if (!submitting) onClose();
  };

  return (
    // Catalyst's panel colors follow only the OS setting (bg-white / dark:bg-zinc-900), so it is
    // cleared and the theme root paints the panel -- that way a forced or host-set scheme applies.
    <Dialog open={open} onClose={close} size={size} className="!bg-transparent !p-0">
      {/* Portaled out of the page, so it carries its own theme root; context still reaches it. */}
      <OtsThemeRoot
        brand={brand}
        colorScheme={colorScheme}
        defaultColorScheme="system"
        data-density={compact ? "compact" : "default"}
        className={`rounded-[inherit] bg-[var(--ots-surface)] ${compact ? "p-4 sm:p-5" : "p-6 sm:p-8"}`}>
        {completion ? (
          <div className="!flex flex-col items-center py-2 text-center">
            <span className={`!flex items-center justify-center rounded-full ${compact ? "size-10" : "size-12"} ${toneClasses.success}`}>
              <span aria-hidden="true" className={`${compact ? "size-6" : "size-7"} [&>svg]:size-full`}>
                {completion.icon ?? <CheckCircleIcon/>}
              </span>
            </span>
            <DialogTitle className={`mt-4 ${titleFontClassName} ${compact ? "text-base" : "text-lg"}`}>{completion.title}</DialogTitle>
            {completion.description && (
              <Description className={`mt-2 max-w-sm text-[var(--ots-text-muted)] ${compact ? "text-xs" : "text-sm"}`}>
                {completion.description}
              </Description>
            )}
            <OtsActionButton variant="primary" size={compact ? "compact" : "default"} className="mt-6 w-full sm:w-auto" onClick={onClose} autoFocus>
              {completion.doneLabel ?? "Done"}
            </OtsActionButton>
          </div>
        ) : (
          <OtsForm
            aria-label={formLabel ?? (typeof title === "string" ? title : "Form")}
            noValidate
            density={compact ? "compact" : "default"}
            onSubmit={() => {
              if (!submitting) onSubmit();
            }}
            actions={<div className={compact ? "mt-1" : "mt-2"}>
              {error != null && error !== false && (
                <OtsInlineAlert tone="danger" size={compact ? "compact" : "default"} className={compact ? "mb-3" : "mb-4"}>
                  {error}
                </OtsInlineAlert>
              )}
              <OtsFormActions
                submitLabel={submitLabel}
                submittingLabel={submittingLabel ?? submitLabel}
                progressLabel={progressLabel}
                submitting={submitting}
                submitDisabled={submitDisabled}
                submitVariant={tone === "danger" ? "destructive" : "primary"}
                cancelLabel={cancelLabel}
                autoFocusCancel={autoFocusCancel}
                onCancel={close}/>
            </div>}>
            {/* `!flex`: a host reset (Mantine's) may override display outside any CSS layer. */}
            <div className={`!flex items-start ${compact ? "gap-3" : "gap-4"}`}>
              {icon && (
                <span className={`!flex shrink-0 items-center justify-center rounded-full ${compact ? "size-8" : "size-10"} ${toneClasses[tone]}`}>
                  <span aria-hidden="true" className={`${compact ? "size-4" : "size-5"} [&>svg]:size-full`}>{icon}</span>
                </span>
              )}
              <div className="min-w-0 pt-0.5">
                <DialogTitle className={`${titleFontClassName} ${compact ? "text-base" : "text-lg"}`}>{title}</DialogTitle>
                {description && (
                  <Description className={`mt-1 text-[var(--ots-text-muted)] ${compact ? "text-xs" : "text-sm"}`}>
                    {description}
                  </Description>
                )}
              </div>
            </div>
            {children}
          </OtsForm>
        )}
      </OtsThemeRoot>
    </Dialog>
  );
};
